import { Link } from "react-router-dom";
import HeroCarousel from "@/pages/home/components/HeroCarousel";
import CategoryEntry from "@/pages/home/components/CategoryEntry";
import MovieCard from "@/components/feature/MovieCard";
import SectionHeading from "@/components/base/SectionHeading";
import { hotTitles, latestTitles, getTitleById, typeLabel } from "@/utils/content";
import { newsList } from "@/mocks/news";
import { specials } from "@/mocks/specials";
import { useRecentlyViewed } from "@/hooks/useLibrary";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";

const picks = [
  {
    id: "dark-river-trail",
    reason: "年度完成度最高的刑侦剧之一。它把整座城市的地下河道拍成了角色，群像与细节都经得起二刷。",
  },
  {
    id: "deep-sea-echo",
    reason: "把悬念藏进声音里，而不是特效里。最后十分钟会让你想重新看一遍开头。",
  },
  {
    id: "interstellar-outpost",
    reason: "一部很安静的科幻剧。十二集的篇幅里，最动人的不是宇宙，而是六个人如何彼此信任。",
  },
];

const stats = [
  { value: "24", label: "部精选影视" },
  { value: "22", label: "个类型标签" },
  { value: "4", label: "期编辑专题" },
  { value: "每日", label: "内容更新" },
];

export default function Home() {
  useDocumentMeta(
    "午夜影院 - 精彩电影电视剧在线播放及资讯平台",
    "午夜影院为您提供丰富的电影和电视剧资源，涵盖多种类别和最新影视资讯，助您轻松发现优质影视内容。"
  );
  const { recent } = useRecentlyViewed();
  const latest = latestTitles(8);
  const latestNews = [...newsList].slice(0, 4);
  const hot = hotTitles(6);

  return (
    <div className="w-full bg-background-50">
      <HeroCarousel />
      <CategoryEntry />

      <section className="mx-auto w-full max-w-[1280px] px-4 pt-14 md:px-6 md:pt-20">
        <SectionHeading
          kicker="最新更新"
          title="刚刚上线的影视作品"
          description="按更新时间排序，收录电影与电视剧的最新条目，点击卡片查看完整资料。"
          actionLabel="查看全部"
          actionTo="/category/movie"
        />
        <div className="grid grid-cols-2 gap-4 animate-fade-up sm:grid-cols-3 md:gap-5 lg:grid-cols-4">
          {latest.map((item) => (
            <MovieCard key={item.id} item={item} />
          ))}
        </div>
      </section>

      <section className="mt-16 w-full bg-background-100 py-14 md:mt-20 md:py-16">
        <div className="mx-auto w-full max-w-[1280px] px-4 md:px-6">
          <SectionHeading
            kicker="编辑精选"
            title="热门影视推荐"
            description="来自编辑部的观看建议。每一部都附上一句推荐理由，帮你决定今晚看什么。"
            actionLabel="更多热门"
            actionTo="/category/tv"
          />
          <div className="grid grid-cols-1 gap-4 animate-fade-up md:grid-cols-3 md:gap-5">
            {picks.map((pick, index) => {
              const item = getTitleById(pick.id);
              if (!item) return null;
              return (
                <article
                  key={pick.id}
                  className="flex gap-4 rounded-lg border border-background-200 bg-background-50 p-4 transition-colors duration-300 hover:border-accent-300"
                >
                  <Link
                    to={`/detail/${item.id}`}
                    className="relative w-24 shrink-0 overflow-hidden rounded-md bg-background-200 sm:w-28"
                  >
                    <div className="aspect-[2/3] w-full">
                      <img
                        src={item.poster}
                        alt={`${item.title}海报`}
                        title={`${item.title} 午夜影院影视详情`}
                        className="h-full w-full object-cover object-top"
                      />
                    </div>
                  </Link>
                  <div className="flex min-w-0 flex-col">
                    <div className="flex items-center gap-2">
                      <span className="font-label text-[10px] uppercase tracking-[0.18em] text-accent-600">
                        No.{String(index + 1).padStart(2, "0")}
                      </span>
                      <span className="rounded-sm bg-secondary-100 px-1.5 py-0.5 text-[10px] text-secondary-900">
                        {typeLabel(item.type)}
                      </span>
                    </div>
                    <Link
                      to={`/detail/${item.id}`}
                      className="mt-1.5 line-clamp-1 font-heading text-[15px] font-semibold text-foreground-950 transition-colors duration-300 hover:text-accent-700"
                    >
                      {item.title}
                    </Link>
                    <p className="mt-1 text-[11px] text-foreground-500">
                      {item.year} · {item.region} · {item.genres.slice(0, 2).join(" / ")}
                    </p>
                    <p className="mt-2 line-clamp-3 text-xs leading-relaxed text-foreground-600">
                      {pick.reason}
                    </p>
                    <div className="mt-auto flex items-center gap-1 pt-3 font-label text-xs text-secondary-700">
                      <i className="ri-star-fill text-secondary-500" />
                      {item.rating.toFixed(1)}
                      <span className="ml-auto flex items-center gap-1 text-[11px] text-foreground-400">
                        热度 {item.heat.toLocaleString()}
                      </span>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>

          <div className="mt-5 grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-6 md:gap-5">
            {hot.slice(3, 6).map((item) => (
              <MovieCard key={item.id} item={item} />
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto w-full max-w-[1280px] px-4 py-14 md:px-6 md:py-20">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1.4fr_1fr] lg:gap-12">
          <div>
            <SectionHeading
              kicker="影视资讯"
              title="最新资讯快讯"
              description="行业动态、影评与访谈，每天为你整理值得读的影视内容。"
              actionLabel="全部资讯"
              actionTo="/news"
            />
            <ul className="divide-y divide-background-200 border-t border-background-200">
              {latestNews.map((item) => (
                <li key={item.id}>
                  <Link
                    to={`/news/detail/${item.id}`}
                    className="group flex gap-4 py-4 transition-colors duration-300 md:gap-5 md:py-5"
                  >
                    <span className="hidden w-16 shrink-0 pt-1 font-label text-xs text-foreground-400 sm:block">
                      {item.date.slice(5)}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="rounded-sm bg-accent-100 px-1.5 py-0.5 text-[10px] text-accent-900">
                          {item.category}
                        </span>
                        <span className="text-[11px] text-foreground-400">{item.author}</span>
                      </div>
                      <h3 className="mt-1.5 line-clamp-1 text-sm font-medium text-foreground-950 transition-colors duration-300 group-hover:text-accent-700 md:text-[15px]">
                        {item.title}
                      </h3>
                      <p className="mt-1 line-clamp-2 text-xs leading-relaxed text-foreground-500">
                        {item.excerpt}
                      </p>
                    </div>
                    <i className="ri-arrow-right-up-line hidden shrink-0 self-center text-foreground-300 transition-colors duration-300 group-hover:text-accent-600 sm:block" />
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <SectionHeading
              kicker="专题策划"
              title="推荐专题"
              actionLabel="全部专题"
              actionTo="/special"
            />
            <div className="grid grid-cols-1 gap-4">
              {specials.slice(0, 2).map((special) => (
                <Link
                  key={special.id}
                  to={`/special/${special.id}`}
                  className="group relative block overflow-hidden rounded-lg bg-primary-950"
                >
                  <div className="h-40 w-full md:h-44">
                    <img
                      src={special.cover}
                      alt={`${special.title}专题封面`}
                      title={`${special.title} 午夜影院专题`}
                      className="h-full w-full object-cover object-center opacity-70 transition-transform duration-700 ease-smooth group-hover:scale-105"
                    />
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-primary-950 via-primary-950/45 to-transparent" />
                  <div className="absolute inset-x-0 bottom-0 p-4">
                    <p className="font-label text-[10px] uppercase tracking-[0.2em] text-secondary-400">
                      {special.month}
                    </p>
                    <h3 className="mt-1 font-heading text-base font-semibold text-background-50">
                      {special.title}
                    </h3>
                    <p className="mt-1 line-clamp-1 text-xs text-background-300/85">
                      {special.subtitle}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="w-full border-y border-background-200 bg-background-100 py-12 md:py-14">
        <div className="mx-auto w-full max-w-[1280px] px-4 md:px-6">
          <SectionHeading
            kicker="为你保留"
            title="最近浏览"
            description="浏览过的影视作品会自动保存在本机，方便你随时回到上一次的页面。"
          />
          {recent.length > 0 ? (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {recent.map((item) => (
                <MovieCard key={item.id} item={item} compact />
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-start gap-3 rounded-lg border border-dashed border-background-300 bg-background-50 px-5 py-6 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-md bg-background-200 text-foreground-500">
                  <i className="ri-history-line" />
                </span>
                <div>
                  <p className="text-sm font-medium text-foreground-900">还没有浏览记录</p>
                  <p className="mt-0.5 text-xs text-foreground-500">
                    打开任意一部影视作品的详情页，这里就会自动记录下来。
                  </p>
                </div>
              </div>
              <Link
                to="/category/movie"
                className="whitespace-nowrap rounded-md border border-background-300 px-3 py-2 text-xs text-foreground-700 transition-colors duration-300 hover:border-accent-400 hover:text-accent-700"
              >
                去发现作品
              </Link>
            </div>
          )}
        </div>
      </section>

      <section className="mx-auto w-full max-w-[1280px] px-4 py-14 md:px-6 md:py-20">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1.1fr_0.9fr] lg:items-center lg:gap-16">
          <div>
            <p className="mb-2 flex items-center gap-2 font-label text-[11px] font-semibold uppercase tracking-[0.22em] text-accent-600">
              <span className="h-[2px] w-6 bg-accent-500" />
              关于午夜影院
            </p>
            <h2 className="font-heading text-2xl font-semibold tracking-tight text-foreground-950 md:text-[32px]">
              一个把选片变简单的内容门户
            </h2>
            <p className="mt-4 max-w-xl text-sm leading-7 text-foreground-600">
              我们把电影与电视剧整理成清晰的分类，用地区、年份、类型和评分搭建起多维筛选，让“今晚看什么”不再是一件靠运气的事。同时，编辑部持续撰写影评、访谈与专题，把作品放回它的语境里。
            </p>
            <ul className="mt-6 space-y-3">
              {[
                "结构化资料：类型、地区、年份、评分一目了然",
                "多维筛选：四个维度组合，快速缩小范围",
                "深度内容：影评、访谈与主题专题持续更新",
              ].map((text) => (
                <li key={text} className="flex items-start gap-2.5 text-sm text-foreground-700">
                  <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary-100 text-primary-800">
                    <i className="ri-check-line text-xs" />
                  </span>
                  {text}
                </li>
              ))}
            </ul>
            <Link
              to="/about"
              className="mt-8 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-900 px-5 py-3 text-sm font-medium text-background-50 transition-colors duration-300 hover:bg-primary-800"
            >
              了解我们
              <i className="ri-arrow-right-line" />
            </Link>
          </div>

          <div className="grid grid-cols-2 gap-3 md:gap-4">
            {stats.map((stat, index) => (
              <div
                key={stat.label}
                className={`rounded-lg border p-5 ${
                  index === 0
                    ? "border-transparent bg-primary-900 text-background-50"
                    : "border-background-200 bg-background-100"
                }`}
              >
                <p
                  className={`font-heading text-2xl font-semibold md:text-3xl ${
                    index === 0 ? "text-secondary-300" : "text-foreground-950"
                  }`}
                >
                  {stat.value}
                </p>
                <p
                  className={`mt-2 text-xs ${
                    index === 0 ? "text-background-300" : "text-foreground-500"
                  }`}
                >
                  {stat.label}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}